import React, { useState, useRef, useEffect } from 'react';
import { FaEllipsisV, FaEye, FaEdit, FaTrash, FaUserCheck, FaUserTimes, FaUserSlash, FaSpinner } from 'react-icons/fa';

const DriverActionMenu = ({ driver, onView, onStatusChange, updatingDriver }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleStatusChange = async (newStatus) => {
    await onStatusChange(driver.id, newStatus);
    setIsOpen(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <FaUserCheck className="h-4 w-4 text-green-600" />;
      case 'inactive':
        return <FaUserTimes className="h-4 w-4 text-gray-600" />;
      case 'suspended':
        return <FaUserSlash className="h-4 w-4 text-red-600" />;
      default:
        return <FaUserCheck className="h-4 w-4 text-blue-600" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      case 'suspended':
        return 'Suspended';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
        title="Actions"
      >
        <FaEllipsisV className="h-4 w-4" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-10">
          <div className="py-1">
            {/* View Details */}
            <button
              onClick={() => {
                onView(driver);
                setIsOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <FaEye className="mr-3 h-4 w-4 text-blue-500" />
              View Details
            </button>

            {/* Current Status Display */}
            <div className="px-4 py-2 text-sm text-gray-500 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span>Current Status:</span>
                <div className="flex items-center">
                  {getStatusIcon(driver.status)}
                  <span className="ml-2 font-medium">{getStatusLabel(driver.status)}</span>
                </div>
              </div>
            </div>

            {/* Status Change Options */}
            <div className="px-4 py-2 text-sm text-gray-500 border-t border-gray-100">
              <span className="block mb-2">Change Status:</span>
              <div className="space-y-1">
                {driver.status !== 'active' && (
                  <button
                    onClick={() => handleStatusChange('active')}
                    disabled={updatingDriver === driver.id}
                    className="w-full text-left px-3 py-1 text-sm text-green-700 hover:bg-green-50 rounded flex items-center disabled:opacity-50"
                  >
                    <FaUserCheck className="mr-2 h-3 w-3" />
                    Activate
                    {updatingDriver === driver.id && <FaSpinner className="ml-auto h-3 w-3 animate-spin" />}
                  </button>
                )}
                
                {driver.status !== 'inactive' && (
                  <button
                    onClick={() => handleStatusChange('inactive')}
                    disabled={updatingDriver === driver.id}
                    className="w-full text-left px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 rounded flex items-center disabled:opacity-50"
                  >
                    <FaUserTimes className="mr-2 h-3 w-3" />
                    Deactivate
                    {updatingDriver === driver.id && <FaSpinner className="ml-auto h-3 w-3 animate-spin" />}
                  </button>
                )}
                
                {driver.status !== 'suspended' && (
                  <button
                    onClick={() => handleStatusChange('suspended')}
                    disabled={updatingDriver === driver.id}
                    className="w-full text-left px-3 py-1 text-sm text-red-700 hover:bg-red-50 rounded flex items-center disabled:opacity-50"
                  >
                    <FaUserSlash className="mr-2 h-3 w-3" />
                    Suspend
                    {updatingDriver === driver.id && <FaSpinner className="ml-auto h-3 w-3 animate-spin" />}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverActionMenu;
